import {
  H5ClimatologyVariable,
  H5WeatherClimatologyData,
  H5WeatherSource,
  H5WeatherVariableGroup,
} from 'src/lib/api/h5';
import {
  ApiObservationTimeSeries,
  Source,
  TimeSeriesData,
  TimeSeriesVariables,
  WeatherHistoricalDailyColumns,
} from 'src/lib/api/series';

// The v2 Source value the chart styles a source's daily climatology band and mean by. Only GEFS
// has one.
const CLIMATOLOGY_CHART_SOURCES: Partial<Record<H5WeatherSource, Source>> = {
  gefs: Source.GEFS_HISTORICAL_DAILY,
};

export const climatologyChartSourceFor = (source: H5WeatherSource): Source | undefined =>
  CLIMATOLOGY_CHART_SOURCES[source];

// The chart draws a climatology's mean and 5-95% band, the columns the v2 historical daily
// source has; the route serves every quantile the climatology holds.
const COLUMN_BY_STATISTIC: Record<
  H5WeatherVariableGroup,
  Partial<Record<string, WeatherHistoricalDailyColumns>>
> = {
  temperature: {
    mean: WeatherHistoricalDailyColumns.TEMPERATURE_MEAN,
    '0.05': WeatherHistoricalDailyColumns.TEMPERATURE_0_05,
    '0.95': WeatherHistoricalDailyColumns.TEMPERATURE_0_95,
  },
  precipitation: {
    mean: WeatherHistoricalDailyColumns.PRECIPITATION_MEAN,
    '0.05': WeatherHistoricalDailyColumns.PRECIPITATION_0_05,
    '0.95': WeatherHistoricalDailyColumns.PRECIPITATION_0_95,
  },
};

const chartColumn = (variable: H5ClimatologyVariable) =>
  COLUMN_BY_STATISTIC[variable.group][
    variable.statistic === 'quantile' ? String(variable.quantile) : variable.statistic
  ];

/**
 * An H5 climatology response as the v2 observation time series the chart draws a historical daily
 * mean from, so the band and mean draw through the same chart as the forecasts. Undefined when
 * the source has no chart identity or the window holds no days.
 */
export function toWeatherClimatologyTimeSeries(
  climatologyData: H5WeatherClimatologyData,
  {siteId}: {siteId: string}
): ApiObservationTimeSeries | undefined {
  const source = climatologyChartSourceFor(climatologyData.source);
  const {validTimes} = climatologyData;
  if (!source || validTimes.length === 0) return undefined;
  const values: TimeSeriesData = {};
  const variables: TimeSeriesVariables = {};
  for (const variable of climatologyData.variables) {
    const column = chartColumn(variable);
    if (!column) continue;
    values[column] = climatologyData.values[variable.name];
    variables[column] = {unit: variable.unit};
  }
  return {
    query: {siteId, source, columns: Object.keys(values)},
    data: values,
    timestamps: validTimes,
    dataTimeRange: [validTimes[0], validTimes[validTimes.length - 1]],
    variables,
  };
}
